import React from 'react';

import { commissions } from '../../utils/affiliateCommissions';
import { WrappedErrorBoundary } from '../WrappedErrorBoundary';

interface IUserInfo {
  firstName: string;
  lastName: string;
  companyName: string;
  birthDate: string;
}

function TOSBusinessPartner({ firstName, lastName, companyName, birthDate }: IUserInfo) {
  return (
    <React.Fragment>
      <div className="tw-self-stretch tw-border-solid tw-h-72 lg:tw-p-6 tw-p-0 tw-border tw-border-gray-300 tw-flex-col tw-justify-start tw-items-start tw-gap-4 tw-flex tw-overflow-y-auto">
        <div className="tw-text-gray-900 tw-text-sm tw-font-normal tw-font-['Lexend'] tw-leading-tight">
          <div>
            <u>Condition date: </u>
            December 01, 2024
          </div>
          <div>
            By accepting these terms and conditions, you enter into a legally binding agreement with LouiseBot, an online chatbot platform. These terms govern
            your participation as an affiliate, paid contributor, or business partner.
          </div>
          <h3>Definitions</h3>
          <div>
            <strong>LouiseBot:</strong>
            <ul>
              <li>Refers to the online chatbot platform developed and operated by LouiseBot.</li>
            </ul>
            <strong>Affiliate:</strong>
            <ul>
              <li>
                <strong>
                  {firstName} {lastName}
                </strong>
                , born on {birthDate}, or their company, <strong>{companyName}</strong>, is an individual or entity promoting LouiseBot&apos;s services in
                exchange for commissions.
              </li>
            </ul>
            <strong>Contributor:</strong>
            <ul>
              <li>
                <strong>
                  {firstName} {lastName}
                </strong>
                , born on {birthDate}, or their company, <strong>{companyName}</strong>, is an individual or entity providing paid services to LouiseBot, such
                as development, content creation, or other technical or creative support.
              </li>
            </ul>
            <strong>Business Partner:</strong>
            <ul>
              <li>
                <strong>
                  {firstName} {lastName}
                </strong>
                , born on {birthDate}, or their company, <strong>{companyName}</strong>, is an entity collaborating with LouiseBot beyond simple affiliation or
                contribution.
              </li>
            </ul>
          </div>
          <h3>Obligations of the Parties</h3>
          <div>
            <strong>Obligations of LouiseBot:</strong>
            <ul>
              <li>Provide tools, resources, and support necessary for your activity as an affiliate, contributor, or business partner.</li>
              <li>Remunerate contributions or commissions based on terms defined in specific agreements.</li>
            </ul>
            <strong>Obligations of the Affiliate:</strong>
            <ul>
              <li>Comply with local, national, and international laws when promoting LouiseBot&apos;s services.</li>
              <li>Avoid fraudulent or unethical practices (spam, misleading advertising, etc.).</li>
            </ul>
            <strong>Obligations of the Contributor:</strong>
            <ul>
              <li>Deliver high-quality services or deliverables in accordance with LouiseBot&apos;s standards.</li>
              <li>Adhere to the deadlines and instructions specified in any agreement or project.</li>
            </ul>
            <strong>Obligations of the Business Partner:</strong>
            <ul>
              <li>Collaborate in good faith to promote and improve LouiseBot&apos;s services.</li>
              <li>Provide reports or data, if required, as part of the collaboration.</li>
            </ul>
          </div>
          <h3>Consent Mode and User Information</h3>
          <div>
            LouiseBot complies with privacy regulations, including GDPR (General Data Protection Regulation) and other applicable data protection laws.
            <br />
            In cases where users explicitly refuse to share information via the Consent Mode, this refusal will be interpreted as the user&apos;s decision to
            block any tracking-related information, including but not limited to:
            <ul>
              <li>Analytics</li>
              <li>Preferences</li>
              <li>Marketing</li>
              <br />
              Affiliates, contributors, and business partners must acknowledge that such explicit refusals from users may limit the availability of analytics
              data or affect performance tracking. LouiseBot is not responsible for any impact on remuneration, tracking systems, or analytics capabilities
              resulting from user choices made under the Consent Mode.
            </ul>
          </div>
          <h3>Eligibility and Validation Criteria for Partnerships</h3>
          <div>
            <strong>Minimum Audience Requirement</strong>
            <ul>
              <br />
              To be considered as a valid partner of LouiseBot, the partner must meet the following minimum audience requirements:
              <li>A verified audience reach of at least 10,000 views, with exceptions possible based on individual profiles.</li>
            </ul>
            <strong>This audience can be demonstrated through:</strong>
            <ul>
              <li>Subscriber count on platforms such as YouTube, Facebook, or all other approved plateforms.</li>
              <li>Verified analytics data from blogs, websites, or social media channels.</li>
              <li>Engagement metrics (e.g., followers, views, comments, etc.) from other platforms.</li>
            </ul>
            The partner must provide verifiable proof of this audience, such as screenshots, links to analytics tools, or platform certifications, upon request
            by LouiseBot. Failure to provide sufficient proof may result in the rejection or suspension of the partnership.
          </div>
          <h3>Remuneration and Payment</h3>
          <div>Affiliate commissions are calculated based on sales or performance metrics specified in the affiliate agreement.</div>
          <div> Contributors are compensated based on approved deliverables under pre-agreed terms.</div>
          <div> Payments to business partners are defined case by case in specific agreements.</div>
          <div> All parties must provide accurate and valid payment information.</div>
          <h3>Payment Terms</h3>
          <div>
            LouiseBot Business Partners have the option to choose from the following payment methods:
            <ul>
              <li>
                <strong>Minimum Payment Amount:</strong>
                Payments will only be processed for amounts equal to or greater than $100. If the total amount due is below this threshold, it will be held
                until the minimum payout requirement is met.
              </li>
              <li>
                <strong>Bank Transfer</strong>
              </li>
              <li>
                <strong>Cryptocurrency Payment</strong>
              </li>
              <li>
                <strong>PayPal Transfer</strong>
              </li>
            </ul>
            <p>To process payments successfully, the Business Partner must provide valid and accurate payment information. The following conditions apply:</p>
            <ul>
              <li>
                <strong>Responsibility for Valid Information:</strong>
                It is the Business Partner&apos;s responsibility to ensure that the information provided is correct, functional, and corresponds to an account
                or institution capable of receiving payments, including high-value transactions.
              </li>
              <li>
                <strong>Failed or Rejected Payments:</strong>
                If a payment cannot be processed due to incorrect or incomplete information, or if it is rejected by the recipient’s financial institution or
                account, a fixed fee of $10 will be deducted to cover administrative and processing costs related to the failed transaction.
              </li>
              <li>
                <strong>Cryptocurrency Payments:</strong>
                Payments via cryptocurrency require particular care, as incorrect or outdated information provided by the Business Partner can result in
                permanent loss of the funds. LouiseBot will not be liable for payments lost due to errors in the information provided by the Business Partner.
                These amounts will not be refunded or compensated under any circumstances.
              </li>
            </ul>
            <p>
              <strong>Important Notes:</strong>
            </p>
            <p>
              <ul>
                <li>Business Partners are solely responsible for keeping their payment information accurate and up-to-date.</li>
                <li>
                  LouiseBot assumes no liability for funds lost due to incorrect payment details or issues related to the receiving account&apos;s conditions.
                </li>
                <li>
                  All payments, regardless of the method chosen, will only be processed after thorough verification of the information provided by the Business
                  Partner.
                </li>
              </ul>
            </p>
          </div>
          <h3>Subscription Validation for Payments</h3>
          <div>
            Payments to partners will only be unlocked after fulfilling the following conditions:
            <ul>
              <li>
                The partner must secure a minimum of 10 eligible and successful customer subscriptions to LouiseBot services. Additionally, payments will only
                be processed if the total amount due meets the minimum threshold of $100. Any earnings below this amount will be held until the minimum payout
                requirement is reached.
              </li>
              <li>
                These subscriptions must meet eligibility criteria, including being confirmed and validated as legitimate (i.e., no fraudulent or fake
                accounts).
              </li>
              <li>Payments will only be released once the minimum threshold of 10 eligible subscriptions is achieved.</li>
              <li>
                If the partner does not achieve the minimum of 10 eligible subscriptions within three months following the last confirmed subscription, the
                funds will be retained, allowing the partner to reach the threshold. However, the partner has a maximum of one year to meet this requirement.
                After this one-year period, the payments for the partnership will be canceled and deemed void
              </li>
            </ul>
          </div>
          <h3>Payment Recurrence Partners</h3>
          <div>
            Payment Recurrence Partners will receive recurring commissions based on the subscription type chosen by the referred customers:
            <ul>
              <li>Monthly or annual payments will be issued in alignment with the customer’s active subscription cycle.</li>
              <li>Recurring commissions will be paid only while the referred customer’s subscription remains active.</li>
              <li>
                Partners must maintain a minimum production of 10 eligible subscriptions (free with real users actively using the solution, or paid) over a
                three-month period for the recurrence to remain active.
              </li>
              <li>If no new subscriptions are produced within a consecutive three-month period, the recurring commissions will be discontinued.</li>
              <li>
                If the customer’s subscription is interrupted (e.g., due to non-payment or cancellation) or if a refund is requested by the customer, the
                recurring commission will:
                <ul>
                  <li>Be immediately discontinued.</li>
                  <li>A deduction of the refunded commission may be applied to future commission payments.</li>
                </ul>
              </li>
            </ul>
            LouiseBot reserves the right to adjust commissions in cases of refunds or chargebacks to ensure alignment with valid revenue contributions.
          </div>
          <h3>Partnership Levels and Commissions</h3>
          <div className="overflow-x-auto tw-mt-3 tw-mb-3">
            <table className="min-w-full border-collapse border border-gray-300 text-left text-sm text-gray-600">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-2">Level</th>
                  <th className="border border-gray-300 px-4 py-2">Cumulative sales</th>
                  <th className="border border-gray-300 px-4 py-2">Subscription commission</th>
                  <th className="border border-gray-300 px-4 py-2">Recurring commission</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">{commissions.by_affiliate.STANDARD.name}</td>
                  <td className="border border-gray-300 px-4 py-2">0 to 10 sales in one month, permanently unlocked</td>
                  <td className="border border-gray-300 px-4 py-2">{commissions.by_affiliate.STANDARD.oneShot_commission * 100}%</td>
                  <td className="border border-gray-300 px-4 py-2">-</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">{commissions.by_affiliate.BRONZE.name}</td>
                  <td className="border border-gray-300 px-4 py-2">10 to 99 sales in one month, permanently unlocked</td>
                  <td className="border border-gray-300 px-4 py-2">{commissions.by_affiliate.BRONZE.oneShot_commission * 100}%</td>
                  <td className="border border-gray-300 px-4 py-2">{commissions.by_affiliate.BRONZE.recurring_commission * 100}%</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-2">{commissions.by_affiliate.SILVER.name}</td>
                  <td className="border border-gray-300 px-4 py-2">100 to 999 sales in one month, permanently unlocked</td>
                  <td className="border border-gray-300 px-4 py-2">{commissions.by_affiliate.SILVER.oneShot_commission * 100}%</td>
                  <td className="border border-gray-300 px-4 py-2">{commissions.by_affiliate.SILVER.recurring_commission * 100}%</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">{commissions.by_affiliate.GOLD.name}</td>
                  <td className="border border-gray-300 px-4 py-2">1,000+ sales in one month, permanently unlocked</td>
                  <td className="border border-gray-300 px-4 py-2">{commissions.by_affiliate.GOLD.oneShot_commission * 100}%</td>
                  <td className="border border-gray-300 px-4 py-2">{commissions.by_affiliate.GOLD.recurring_commission * 100}%</td>
                </tr>
              </tbody>
            </table>
          </div>
          <h3>Contributor Commissions</h3>
          <div className="overflow-x-auto tw-mt-3 tw-mb-3">
            <table className="min-w-full border-collapse border border-gray-300 text-left text-sm text-gray-600">
              <thead>
                <tr className="bg-gray-100">
                  <th className="border border-gray-300 px-4 py-2">Contribution level</th>
                  <th className="border border-gray-300 px-4 py-2">Conditions</th>
                  <th className="border border-gray-300 px-4 py-2">Subscription commission</th>
                  <th className="border border-gray-300 px-4 py-2">Recurring commission</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">
                    {commissions.for_contributor.STANDARD.name}
                    <br />
                    <small>(0 to 99 active contributions)</small>
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    <small>
                      <i>assigned case-by-case</i>
                    </small>
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {commissions.for_contributor.STANDARD.oneShot_amount}$ is paid for each paid plan that uses the contributor&apos;s contribution.
                  </td>
                  <td className="border border-gray-300 px-4 py-2">-</td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">
                    {commissions.for_contributor.BRONZE.name}
                    <br />
                    <small>(100 to 999 active contributions)</small>
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    <small>
                      <i>assigned case-by-case</i>
                    </small>
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {commissions.for_contributor.BRONZE.oneShot_amount}$ is paid for each paid plan that uses the contributor&apos;s contribution.
                  </td>
                  <td className="border border-gray-300 px-4 py-2">-</td>
                </tr>
                <tr className="bg-gray-50">
                  <td className="border border-gray-300 px-4 py-2">
                    {commissions.for_contributor.SILVER.name}
                    <br />
                    <small>(1000 to 99999 active contributions)</small>
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    <small>
                      <i>assigned case-by-case</i>
                    </small>
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {commissions.for_contributor.SILVER.oneShot_amount}$ is paid for each paid plan that uses the contributor&apos;s contribution.
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {commissions.for_contributor.SILVER.recurring_amount}$ is paid for each paid plan that uses the contributor&apos;s contribution.
                  </td>
                </tr>
                <tr>
                  <td className="border border-gray-300 px-4 py-2">
                    {commissions.for_contributor.GOLD.name}
                    <br />
                    <small>(100,000+ active contributions)</small>
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    <small>
                      <i>assigned case-by-case</i>
                    </small>
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {commissions.for_contributor.GOLD.oneShot_amount}$ is paid for each paid plan that uses the contributor&apos;s contribution.
                  </td>
                  <td className="border border-gray-300 px-4 py-2">
                    {commissions.for_contributor.GOLD.recurring_amount}$ is paid for each paid plan that uses the contributor&apos;s contribution.
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
          <h3>Definition of an Active Contributor</h3>
          <div>
            <p>
              An Active Contributor is an individual who has contributed to the platform, and whose contributions are regularly utilized by users holding a paid
              subscription plan.
            </p>
            <ul>
              <li>
                Examples of Active Contributors on the platform include individuals who have created and shared templates, tools, or other resources that are
                actively used by other users.
              </li>
            </ul>
            <p>
              Each initiative or contribution must first be suggested to and approved by an account manager to ensure alignment with platform standards and user
              needs.
            </p>
            <p>
              Contributions must demonstrate consistent engagement and add value to the experience of the platform&apos;s active paying users to qualify as
              &quot;active.&quot;
            </p>
          </div>
          <h3>Contributor Level Assignment Policy</h3>
          <div>
            <p>
              The assignment of commission levels is not based on sales performance. Instead, it is determined by our Business Account team on a case-by-case
              basis, taking into account the contributor&apos;s level of involvement with the product. This includes their understanding of the solution,
              contribution to its improvement, advocacy efforts, and active participation in promoting and supporting the product within the community.
            </p>
          </div>
          <h3>Partner Benefits Activation</h3>
          <div>
            The partner’s benefits, including payment processing, discounted rates, or other incentives, will be activated only after:
            <ul>
              <li>Proof of a qualified audience is submitted and approved.</li>
              <li>A minimum of 10 validated customer subscriptions is achieved.</li>
              <li>
                LouiseBot reserves the right to review and adjust the eligibility criteria based on evolving standards, market trends, and business needs.
              </li>
            </ul>
          </div>
          <h3>Purpose of These Measures</h3>
          <div>
            These requirements are designed to:
            <ul>
              <li>Maintain the integrity of the partnership program.</li>
              <li>Prevent abuse by users falsely claiming partner status to gain unwarranted benefits.</li>
            </ul>
            Ensure that valid partners are adequately rewarded for their contributions to LouiseBot&apos;s growth.
          </div>
          <h3>Liability and Warranties</h3>
          <div>
            <strong>Liability of LouiseBot:</strong>
            <ul>
              <li>LouiseBot will make reasonable efforts to ensure the continuous operation and quality of its services.</li>
              <li>
                LouiseBot shall not be held liable for indirect damages, lost profits, or other harm resulting from the use of its services or the partnership.
              </li>
            </ul>
            <ul>
              <strong>Liability of Affiliates, Contributors, and Partners:</strong>
              <li>
                You agree to indemnify LouiseBot against any liability arising from violations of laws, third-party rights, or non-compliance with these terms.
              </li>
            </ul>
          </div>
          <h3>Intellectual Property</h3>
          <div>
            <ul>
              <li>All rights, trademarks, content, and technologies related to LouiseBot remain the exclusive property of LouiseBot.</li>
              <li>Contributions (code, content, design, etc.) created for LouiseBot become, unless otherwise agreed, the property of LouiseBot.</li>
            </ul>
          </div>
          <h3>Confidentiality</h3>
          <div>
            <ul>
              <li>Any information exchanged as part of this partnership must remain confidential.</li>
              <li>Personal data collected will be used in accordance with our Privacy Policy.</li>
            </ul>
          </div>
          <h3>Termination</h3>
          <div>
            <ul>
              <li>
                LouiseBot reserves the right to terminate any collaboration at any time and without prior notice if we determine that the collaboration is no
                longer ethical, profitable, or for any other reason deemed valid, without providing justification.
              </li>
              <li>Upon termination, all obligations regarding payment and the return of confidential materials or information remain in effect.</li>
            </ul>
          </div>
          <h3>Anti-Fraud Measures</h3>
          <div>
            LouiseBot retains the right to:
            <ul>
              <li>Suspend payments or suspend partnerships if suspicious activities are detected</li>
              <li>Conduct additional verifications on the legitimacy of the partner&apos;s audience or referred subscriptions.</li>
            </ul>
            Partners found to be engaging in fraudulent activities, such as falsifying metrics or using non-compliant marketing practices, will face immediate
            termination of their partnership and potential legal action.
          </div>
          <h3>Dispute Resolution</h3>
          <div>
            <ul>
              <li>In the event of a dispute, the parties agree to seek an amicable resolution within [30] days of written notification of the issue.</li>
              <li>
                If no amicable resolution is reached, any dispute arising from these terms shall be submitted exclusively to the competent courts of Lausanne,
                Switzerland.
              </li>
              <li>Swiss law shall exclusively govern these terms.</li>
              <li>
                LouiseBot disclaims any liability for indirect or consequential damages resulting from misunderstandings or contractual disputes. In all cases,
                LouiseBot&apos;s liability is strictly limited to the total amount paid over the last six months by the concerned party as part of this
                collaboration.
              </li>
            </ul>
          </div>
          <h3>Miscellaneous</h3>
          <div>
            <ul>
              <li>If any clause in these terms is deemed invalid, it will not affect the validity of the other clauses.</li>
              <li>These terms constitute the entire agreement between you and LouiseBot.</li>
            </ul>
          </div>
          <h3>Amendments to These Terms</h3>
          <div>
            LouiseBot reserves the right to update or modify these terms and conditions at any time to reflect changes in our policies, legal requirements, or
            business practices. Any such updates will take effect immediately upon publication.
          </div>
          <div>
            It is the responsibility of affiliates, contributors, and business partners to review these terms regularly to stay informed of any changes.
            Continued participation in the partnership program constitutes acceptance of the updated terms.
          </div>
        </div>
      </div>
    </React.Fragment>
  );
}

export default WrappedErrorBoundary(TOSBusinessPartner);
